import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import {
  addManualMember,
  addTeamMember,
  ManualMemberParams,
  removeTeamMember,
} from 'app/frontend/base/api/team'
import { displayErrorToast } from 'app/frontend/base/helpers'
import { useTeam } from 'app/frontend/base/config/context/team'
import AddCharacterModal, { AddCharacterModalProps } from './AddCharacterModal'
import { useGameVersion } from 'app/frontend/base/hooks/useGameVersion'

type Props = Omit<
  AddCharacterModalProps,
  | 'mode'
  | 'existingCharacterIds'
  | 'onAddCharacter'
  | 'isAddingAny'
  | 'addingCharacterId'
  | 'onRemoveCharacter'
  | 'isRemovingAny'
  | 'removingMemberId'
  | 'onCreateCharacter'
  | 'isCreatingCharacter'
  | 'onOpenChange'
>

const AddTeamMemberModal = ({ open, setOpen, ...props }: Props) => {
  const { t } = useTranslation()
  const { team, refetchTeamDetails } = useTeam()
  const queryClient = useQueryClient()
  const gameVersion = useGameVersion()

  const addMemberMutation = useMutation({
    mutationFn: async (params: {
      characterId: number
      specId?: number
      role?: string
      teamRankId?: number
    }) => {
      return await addTeamMember(
        team.id,
        params.characterId,
        params.specId,
        params.role,
        params.teamRankId,
      )
    },
    onSuccess: (newData) => {
      queryClient.setQueryData(['team', { path: team.path }], newData)
      toast.success(t('dashboard.team.roster.member-added'))
    },
    onError: (error) => {
      displayErrorToast(error, t)
    },
  })

  const addManualMemberMutation = useMutation({
    mutationFn: async ({
      params,
    }: {
      params: ManualMemberParams
      onSuccess?: () => void
    }) => {
      return await addManualMember(team.id, params)
    },
    onSuccess: (newData, { onSuccess }) => {
      queryClient.setQueryData(['team', { path: team.path }], newData)
      toast.success(t('dashboard.team.roster.member-added'))
      onSuccess?.()
    },
    onError: (error) => {
      displayErrorToast(error, t)
    },
  })

  const removeMemberMutation = useMutation({
    mutationFn: async (memberId: number) => {
      return await removeTeamMember(team.id, memberId)
    },
    onSuccess: (newData) => {
      queryClient.setQueryData(['team', { path: team.path }], newData)
      toast.success(
        t('dashboard.team.roster.member-removed', {
          name: 'Member',
        }),
      )
    },
    onError: (error) => {
      displayErrorToast(error, t)
    },
  })

  return (
    <AddCharacterModal
      {...props}
      open={open}
      setOpen={setOpen}
      mode='roster'
      existingCharacterIds={
        new Set(
          (team.members ?? []).map((member) => member.characterReference.id),
        )
      }
      onAddCharacter={(params) => addMemberMutation.mutate(params)}
      isAddingAny={addMemberMutation.isPending}
      addingCharacterId={addMemberMutation.variables?.characterId}
      onRemoveCharacter={(memberId) => removeMemberMutation.mutate(memberId)}
      isRemovingAny={removeMemberMutation.isPending}
      removingMemberId={removeMemberMutation.variables}
      onCreateCharacter={
        gameVersion.has('manual_characters')
          ? (params, onSuccess) =>
              addManualMemberMutation.mutate({ params, onSuccess })
          : undefined
      }
      isCreatingCharacter={addManualMemberMutation.isPending}
      onOpenChange={() => refetchTeamDetails()}
    />
  )
}

export default AddTeamMemberModal
